import {
  isSequenceApiError,
  isSequenceApiErrorResponse,
  type SequenceError
} from '@sequencehq/api'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import { match } from 'ts-pattern'

/**
 * Why billing refused to execute an amendment, when the execute modal can
 * put it right and let the user confirm again:
 *
 * - `amendmentPastExecuteBy`: the date it was due to take effect has passed,
 *   by billing's today. Billing counts days in UTC, so near midnight this can
 *   be a different day to the browser's.
 * - `amendmentPhaseAlignment`: it would change pricing part way through a
 *   billing period, which the billing period reset avoids.
 */
export type AmendmentRefusal =
  | { code: 'amendmentPastExecuteBy'; today: Date }
  | { code: 'amendmentPhaseAlignment' }

const REFUSAL_CODES: string[] = [
  'amendmentPastExecuteBy',
  'amendmentPhaseAlignment'
] satisfies AmendmentRefusal['code'][]

export const getAmendmentRefusal = (
  error: unknown
): AmendmentRefusal | undefined => {
  if (
    !isSequenceApiError(error) ||
    !isSequenceApiErrorResponse(error.response)
  ) {
    return
  }

  const refusal = findRefusal(error.response.error.errors)

  return match(refusal?.code)
    .with('amendmentPastExecuteBy', code => {
      const today = getDetailDate(refusal?.detail, 'today')

      return today && { code, today }
    })
    .with('amendmentPhaseAlignment', code => ({ code }))
    .otherwise(() => undefined)
}

const findRefusal = (
  errors: SequenceError[] | undefined
): SequenceError | undefined =>
  (Array.isArray(errors) ? errors : []).reduce<SequenceError | undefined>(
    (found, error) =>
      found ??
      (REFUSAL_CODES.includes(error.code) ? error : findRefusal(error.errors)),
    undefined
  )

const getDetailDate = (detail: SequenceError['detail'], key: string) => {
  const value = typeof detail === 'object' ? detail[key] : undefined

  return typeof value === 'string' ? apiDatesAdapters.fromApi(value) : undefined
}
